import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { SectionHeader } from '../../features/section-components/SectionHeader'
import { UserProfile } from '../../features/user/UserProfile'
import { Button } from '../../features/section-components/Button'
import styles from './MyAccount.module.scss'
import { LoadingIcon } from '../../features/section-components/LoadingIcon'
import MarginWrapper from '../../features/wrappers/MarginWrapper'
import '../../util/i18n'
import JNJFooter from '../../features/jnj-footer/JNJFooter'
import Alert from '../../features/error/Alert'
import {
  selectUserCallError
} from '../../features/user/userSlice'
import { PictureUpload } from '../../features/PictureUpload/index'
import AccountItemContainer from './components/AccountItemContainer'
import FlagAccountItemContainer from './components/FlagAccountItemContainer'
import LinkAccountItemContainer from './components/LinkAccountItemContainer'

function MyAccountDesktop({
  t,
  userObj,
  userProfilePictureData,
  fullName,
  isPageLoading,
  renderData,
  formatPhoneToNational,
  preferredLanguage,
  languageOptions,
  setAlertVisible,
  editAccountComponent,
  toggleEditAccount,
  showEditAccount,
  showProfileInfo,
  handlePhotoDelete,
  phoneNumber,
  editNameStatus,
  editLanguageStatus,
  editSecondaryEmailStatus,
}) {
  const scrollToTop = () => {
    window.scrollTo(0, 0)
  }
  const userError = useSelector(selectUserCallError)
  const initials = `${
    userObj && userObj.firstName ? userObj.firstName[0] : ''
  }${userObj && userObj.lastName ? userObj.lastName[0] : ''}`

  const location = useLocation()
  useEffect(() => {
    if (location?.state?.editNameStatus || location?.state?.editLanguageStatus
      || location?.state?.editSecondaryEmailStatus) {
      window.history.replaceState({}, document.title, location.pathname)
    }
  }, [])

  return (
    <MarginWrapper>
      <LoadingIcon isLoading={isPageLoading}>
        <div className={styles['parent-container']}>
          <div className={styles['my-account-container']}>
            <div className={styles['account-header']}>
              <SectionHeader text={t('AccountInfoHeader')} size="large" />
              {userError && (
                <div className={styles['alert-container']}>
                  <Alert
                    message={t('error-saveFailed')}
                    onClose={() => setAlertVisible(false)}
                    alertStyle="error-alert"
                    headerText={t('error-title')}
                    blurb
                  />
                </div>
              )}
              {editNameStatus && (
                <div className={styles['alert-container']}>
                  <Alert
                    message={t('success-edit-name')}
                    onClose={() => setAlertVisible(false)}
                    alertStyle="success-alert"
                    headerText={t('success-edit-name-header')}
                  />
                </div>
              )}
              {editLanguageStatus && (
                <div className={styles['alert-container']}>
                  <Alert
                    message={t('success-edit-language')}
                    onClose={() => setAlertVisible(false)}
                    alertStyle="success-alert"
                    headerText={t('success-edit-language-header')}
                  />
                </div>
              )}
              {editSecondaryEmailStatus && (
                <div className={styles['alert-container']}>
                  <Alert
                    message={t('success-edit-secondary-email')}
                    onClose={() => setAlertVisible(false)}
                    alertStyle="success-alert"
                    headerText={t('success-edit-secondary-header')}
                  />
                </div>
              )}
            </div>
            <div className={styles['profile-info-container']}>
              <div className={styles['profile-header-container']}>
                <div className={styles['profile-picture-container']}>
                  <div className={styles['profile-picture-wrapper']}>
                    <UserProfile
                      isInteractive={false}
                      profilePictureData={userProfilePictureData}
                      initials={initials}
                    />
                  </div>
                  <div className={styles['profile-picture-support-text']}>
                    <PictureUpload />
                    {t('support-text-profile-pic')}
                    {userProfilePictureData ? (
                      <Button
                        text={t('button-delete-photo')}
                        buttonStyle="cta-yellow-oval"
                        disabled={false}
                        icon={<DeleteOutlineIcon />}
                        iconSize="small"
                        onClick={handlePhotoDelete}
                      />
                    ) : null}
                  </div>
                </div>
                <div className={styles['account-header-support-text']}>
                  <span>{t('support-text-account')}</span>
                </div>
              </div>
              {showProfileInfo && (
                <>
                  <AccountItemContainer
                    label="label-name"
                    link="/edit-name"
                    onClick={scrollToTop}
                    renderData={renderData}
                    t={t}
                    value={fullName}
                    button
                  />

                  <AccountItemContainer
                    label="label-email"
                    onClick={toggleEditAccount}
                    renderData={renderData}
                    t={t}
                    value={userObj.email}
                  />

                  {(userObj.secondEmail || !userObj.secondEmail) && (
                    <AccountItemContainer
                      label="label-second-email"
                      onClick={toggleEditAccount}
                      renderData={renderData}
                      t={t}
                      value={userObj.secondEmail}
                      button
                    />
                  )}

                  {(phoneNumber || !phoneNumber) && (
                    <LinkAccountItemContainer
                      onClick={scrollToTop}
                      renderData={renderData}
                      t={t}
                      link="/edit-phone"
                      formatPhoneToNational={formatPhoneToNational}
                      phoneNumber={phoneNumber}
                    />
                  )}
                  <FlagAccountItemContainer
                    preferredLanguage={preferredLanguage}
                    renderData={renderData}
                    t={t}
                    link="/edit-language"
                    onClick={scrollToTop}
                    languageOptions={languageOptions}
                  />
                </>
              )}
              {showEditAccount && editAccountComponent}
            </div>
            {/* <div
              className="password-container"
              style={{ lineHeight: '1.5', marginTop: '40px' }}
            >
              {t('support-text-pass', { email: userObj.email })}
              <div className={styles.buttons}>
                <Grid className={styles['manage-button']}>
                  <Link
                    to="/reset-password"
                    style={{ display: 'inline-block' }}
                  >
                    <Button
                      text={t('button-reset-password')}
                      buttonStyle="cta-grey-oval"
                      onClick={handleEditPhoneClick}
                    />
                  </Link>
                </Grid>
              </div>
            </div> */}
          </div>
        </div>
        <div>
          <JNJFooter />
        </div>
      </LoadingIcon>
    </MarginWrapper>
  )
}

MyAccountDesktop.propTypes = {
  t: PropTypes.func.isRequired,
  userObj: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    secondEmail: PropTypes.string,
    mobilePhone: PropTypes.string,
    created: PropTypes.string,
    preferredLanguage: PropTypes.string,
  }).isRequired,
  userProfilePictureData: PropTypes.string,
  fullName: PropTypes.string.isRequired,
  isPageLoading: PropTypes.bool.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  renderData: PropTypes.func.isRequired,
  formatPhoneToNational: PropTypes.func.isRequired,
  preferredLanguage: PropTypes.string.isRequired,
  languageOptions: PropTypes.objectOf(PropTypes.string).isRequired,
  setAlertVisible: PropTypes.func.isRequired,
  editAccountComponent: PropTypes.element,
  toggleEditAccount: PropTypes.func.isRequired,
  showEditAccount: PropTypes.bool.isRequired,
  showProfileInfo: PropTypes.bool.isRequired,
  handlePhotoDelete: PropTypes.func.isRequired,
  editNameStatus: PropTypes.bool.isRequired,
  editLanguageStatus: PropTypes.bool.isRequired,
  editSecondaryEmailStatus: PropTypes.bool.isRequired
}

MyAccountDesktop.defaultProps = {
  userProfilePictureData: '',
  editAccountComponent: null,
}

export default MyAccountDesktop
